/**
 * WervingSjablonen — beheer van de standaardmails voor ikwilwerken@ (tabel werving_sjablonen).
 *
 * Herbruikbaar: Admin → Algemene mailboxen → ikwilwerken@ gebruikt hem, en de wervingsmodule kan hem
 * ook tonen met een eigen `api` (zie wervingApi(basis) in pages/AdminSettings/algemeneMailboxen/api.js).
 *
 * Props:
 *  - api: { sjablonen, maak, wijzig, verwijder, voorbeeld }
 *  - kanBewerken: boolean (recht werving:edit)
 *  - categorieLabels: { categorie: label } (optioneel)
 *
 * Vangnetten: actief aan/uit → ongedaan maken; verwijderen → bevestig; opslaan → geen.
 * Rustig: één lijst, één open bewerkpaneel tegelijk, voorbeeld pas op verzoek.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, CircularProgress, MenuItem, Switch, TextField, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { bevestig } from '../utils/bevestig';
import { biedOngedaanAan } from '../utils/ongedaan';
import { onbekendePlaceholders, vulVoorbeeld } from '../pages/AdminSettings/algemeneMailboxen/api';

const GRIJS = '#556071';
const RAND = '#e5e9eb';
const LEEG = { naam: '', categorie: '', onderwerp: '', body: '', actief: false };

function Bewerkpaneel({ sjabloon, categorieen, labels, placeholders, api, onOpgeslagen, onSluit }) {
  const [vorm, setVorm] = useState(() => ({ ...LEEG, categorie: categorieen[0] || '', ...(sjabloon || {}) }));
  const [bezig, setBezig] = useState(false);
  const [toonVoorbeeld, setToonVoorbeeld] = useState(false);
  const [huidig, setHuidig] = useState(null);
  const bodyRef = useRef(null);
  const nieuw = !sjabloon?.id;
  const onbekend = useMemo(
    () => onbekendePlaceholders(`${vorm.onderwerp} ${vorm.body}`, placeholders), [vorm.onderwerp, vorm.body, placeholders]);

  const voegIn = (p) => {
    const el = bodyRef.current;
    const tekst = `{${p}}`;
    if (!el) { setVorm((v) => ({ ...v, body: `${v.body}${tekst}` })); return; }
    const start = el.selectionStart ?? vorm.body.length;
    const eind = el.selectionEnd ?? start;
    const body = `${vorm.body.slice(0, start)}${tekst}${vorm.body.slice(eind)}`;
    setVorm((v) => ({ ...v, body }));
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(start + tekst.length, start + tekst.length); });
  };

  const opslaan = async () => {
    setBezig(true);
    try {
      const body = { naam: vorm.naam, categorie: vorm.categorie, onderwerp: vorm.onderwerp, body: vorm.body };
      const uit = nieuw ? await api.maak({ ...body, actief: false }) : await api.wijzig(sjabloon.id, body);
      toast.success(nieuw ? 'Sjabloon toegevoegd (staat nog uit)' : 'Sjabloon opgeslagen');
      onOpgeslagen(uit);
    } catch (e) {
      toast.error(e.message);
    } finally { setBezig(false); }
  };

  const laadHuidig = async () => {
    try { setHuidig(await api.voorbeeld(vorm.categorie)); } catch (e) { toast.error(e.message); }
  };

  return (
    <Box sx={{ py: 2.5, px: { xs: 0, md: 1 }, display: 'grid', gap: 2, maxWidth: 760 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2 }}>
        <TextField size="small" label="Naam" value={vorm.naam} onChange={(e) => setVorm({ ...vorm, naam: e.target.value })} />
        <TextField size="small" select label="Categorie" value={vorm.categorie}
          onChange={(e) => setVorm({ ...vorm, categorie: e.target.value })}>
          {categorieen.map((c) => <MenuItem key={c} value={c}>{labels[c] || c}</MenuItem>)}
        </TextField>
      </Box>
      <TextField size="small" label="Onderwerp" value={vorm.onderwerp}
        onChange={(e) => setVorm({ ...vorm, onderwerp: e.target.value })} />
      <TextField label="Tekst" multiline minRows={8} value={vorm.body} inputRef={bodyRef}
        onChange={(e) => setVorm({ ...vorm, body: e.target.value })} />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: '0.78rem', color: GRIJS, mr: 0.5 }}>Invoegen:</Typography>
        {placeholders.map((p) => (
          <Button key={p} size="small" onClick={() => voegIn(p)}
            sx={{ textTransform: 'none', minWidth: 0, px: 1, color: GRIJS, fontFamily: 'monospace', fontSize: '0.78rem' }}>
            {`{${p}}`}
          </Button>
        ))}
      </Box>
      {onbekend.length > 0 && (
        <Typography sx={{ fontSize: '0.8rem', color: '#b91c1c' }}>
          Onbekende placeholder: {onbekend.map((p) => `{${p}}`).join(', ')}. Gebruik alleen de knoppen hierboven.
        </Typography>
      )}
      <Typography sx={{ fontSize: '0.78rem', color: GRIJS }}>
        {'{persoonlijk}'} is één zin die de AI per sollicitatie schrijft (zonder toezeggingen). Concepten staan in
        ikwilwerken@ → Concepten; versturen doe je zelf in Outlook.
      </Typography>

      <Box>
        <Button size="small" onClick={() => setToonVoorbeeld((x) => !x)} sx={{ textTransform: 'none', px: 0 }}>
          {toonVoorbeeld ? 'Voorbeeld verbergen' : 'Voorbeeld bekijken'}
        </Button>
        {toonVoorbeeld && (
          <Box sx={{ mt: 1, p: 2, bgcolor: '#f8f9fa', borderRadius: 1.5 }}>
            <Typography sx={{ fontSize: '0.78rem', color: GRIJS, mb: 0.5 }}>Zo ziet deze tekst eruit (voorbeeldwaarden):</Typography>
            <Typography sx={{ fontWeight: 600, fontSize: '0.88rem', mb: 1 }}>{vulVoorbeeld(vorm.onderwerp)}</Typography>
            <Typography component="div" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.86rem' }}>{vulVoorbeeld(vorm.body)}</Typography>
            <Button size="small" onClick={laadHuidig} sx={{ textTransform: 'none', px: 0, mt: 1.5 }}>
              Wat gaat er nu uit voor deze categorie? (opgeslagen, actief sjabloon)
            </Button>
            {huidig && (
              <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${RAND}` }}>
                {huidig.sjabloon ? (
                  <>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.86rem', mb: 0.5 }}>{huidig.onderwerp}</Typography>
                    <Typography component="div" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.84rem', color: GRIJS }}>{huidig.concept}</Typography>
                  </>
                ) : (
                  <Typography sx={{ fontSize: '0.84rem', color: GRIJS }}>Geen actief sjabloon voor deze categorie — er wordt dan geen concept gemaakt.</Typography>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button variant="contained" disableElevation onClick={opslaan}
          disabled={bezig || !vorm.naam.trim() || !vorm.body.trim() || onbekend.length > 0}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          {bezig ? <CircularProgress size={16} color="inherit" /> : 'Opslaan'}
        </Button>
        <Button onClick={onSluit} sx={{ textTransform: 'none', color: GRIJS }}>Annuleren</Button>
      </Box>
    </Box>
  );
}

export default function WervingSjablonen({ api, kanBewerken = false, categorieLabels = {} }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null); // id | 'nieuw' | null

  const laad = useCallback(async () => {
    try { setData(await api.sjablonen()); } catch (e) { toast.error(e.message); setData({ sjablonen: [], placeholders: [], categorieen: [] }); }
  }, [api]);
  useEffect(() => { laad(); }, [laad]);

  const zetActief = async (s, actief) => {
    try {
      await api.wijzig(s.id, { actief });
      await laad();
      biedOngedaanAan(`${s.naam}: ${actief ? 'aan' : 'uit'}`, async () => { await api.wijzig(s.id, { actief: s.actief }); await laad(); });
    } catch (e) { toast.error(e.message); }
  };

  const verwijder = async (s) => {
    const ok = await bevestig({
      titel: 'Sjabloon verwijderen?',
      tekst: `"${s.naam}" wordt definitief verwijderd. Staat er geen ander actief sjabloon voor deze categorie, dan maakt de tool daar geen concept meer voor.`,
      knop: 'Verwijderen', gevaarlijk: true,
    });
    if (!ok) return;
    try { await api.verwijder(s.id); toast.success('Verwijderd'); setOpen(null); await laad(); } catch (e) { toast.error(e.message); }
  };

  if (!data) return <CircularProgress size={20} sx={{ color: '#adb3b5' }} />;
  const lijst = data.sjablonen || [];
  const cats = data.categorieen || [];
  const ph = data.placeholders || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Sjablonen</Typography>
        {kanBewerken && open !== 'nieuw' && (
          <Button size="small" onClick={() => setOpen('nieuw')} sx={{ textTransform: 'none' }}>Nieuw sjabloon</Button>
        )}
      </Box>
      {open === 'nieuw' && (
        <Box sx={{ borderTop: `1px solid ${RAND}` }}>
          <Bewerkpaneel categorieen={cats} labels={categorieLabels} placeholders={ph} api={api}
            onOpgeslagen={() => { setOpen(null); laad(); }} onSluit={() => setOpen(null)} />
        </Box>
      )}
      {lijst.length === 0 && open !== 'nieuw' && (
        <Typography sx={{ fontSize: '0.86rem', color: GRIJS, py: 2 }}>Nog geen sjablonen.</Typography>
      )}
      {lijst.map((s) => (
        <Box key={s.id} sx={{ borderTop: `1px solid ${RAND}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.25 }}>
            <Box sx={{ flex: 1, minWidth: 0, cursor: kanBewerken ? 'pointer' : 'default' }}
              onClick={() => kanBewerken && setOpen(open === s.id ? null : s.id)}>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: s.actief ? '#2d3335' : GRIJS }}>{s.naam}</Typography>
              <Typography sx={{ fontSize: '0.78rem', color: GRIJS }}>
                {categorieLabels[s.categorie] || s.categorie}{s.onderwerp ? ` · ${s.onderwerp}` : ''}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '0.78rem', color: GRIJS }}>{s.actief ? 'Actief' : 'Uit'}</Typography>
            <Switch size="small" checked={!!s.actief} disabled={!kanBewerken}
              onChange={(e) => zetActief(s, e.target.checked)} inputProps={{ 'aria-label': `${s.naam} actief` }} />
            {kanBewerken && (
              <Button size="small" onClick={() => verwijder(s)} sx={{ textTransform: 'none', color: GRIJS, minWidth: 0 }}>
                Verwijderen
              </Button>
            )}
          </Box>
          {open === s.id && (
            <Bewerkpaneel sjabloon={s} categorieen={cats} labels={categorieLabels} placeholders={ph} api={api}
              onOpgeslagen={() => { setOpen(null); laad(); }} onSluit={() => setOpen(null)} />
          )}
        </Box>
      ))}
    </Box>
  );
}
